import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Scale } from 'lucide-react';

import {
  atualizarProcessosCliente,
  fetchClienteProcessos,
  type ClienteProcesso,
} from '@/api/clientes';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BuscaInteligente } from '@/components/ui/smart-search';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { useSmartSearch } from '@/hooks/useSmartSearch';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { filterBySmartSearch, type SearchStrictness, type SmartSearchFields } from '@/lib/search/smartSearch';
import { cliqueDoMeioNaLinha, useAbrirAutos } from '@/hooks/useAbrirAutos';

/**
 * O texto pesquisável é só NOME DE PARTE, de propósito: com o número dentro,
 * "banco 9" casaria o 9 do número. O número entra à parte, pelo
 * `matchesProcessNumber` (pergunta só de número, a partir de 4 dígitos).
 */
const CLIENT_PROCESS_SEARCH_FIELDS: SmartSearchFields<ClienteProcesso> = {
  text: (p) => [...(p.partes ?? []), p.parte_adversa?.nome ?? ''].filter(Boolean).join('\n'),
  processNumber: (p) => p.numero,
};

/** Filtro rápido: número (com ou sem pontuação) ou nomes das partes, pelas formas da busca inteligente. */
export function filtrarProcessos(
  processos: ClienteProcesso[],
  termo: string,
  strictness: SearchStrictness = 'flexible',
): ClienteProcesso[] {
  return filterBySmartSearch(processos, termo, strictness, CLIENT_PROCESS_SEARCH_FIELDS);
}

const EMPTY: ClienteProcesso[] = [];

type AvisoDaBusca = 'requer_pdpj' | 'cliente_sem_documento' | 'documento_invalido';

const AVISOS: Record<AvisoDaBusca, string> = {
  requer_pdpj: 'Conecte-se à PDPJ para buscar os processos deste cliente.',
  cliente_sem_documento: 'Cadastre o CPF ou CNPJ do cliente para buscar os processos dele.',
  documento_invalido: 'O CPF ou CNPJ do cliente não é válido. Corrija o cadastro para buscar os processos dele.',
};

export function ClienteProcessosTab({
  clienteId,
  somenteLeitura = false,
}: {
  clienteId: string;
  /** Cliente de colega (visão do escritório): buscar processos no tribunal é escrita. */
  somenteLeitura?: boolean;
}) {
  const abrirAutos = useAbrirAutos();
  const queryClient = useQueryClient();
  const { showSuccess, showWarning, showError } = useNotification();
  // O que impediu a busca no Data Lake e só o usuário resolve: fica visível, não some num toast.
  const [aviso, setAviso] = useState<AvisoDaBusca | null>(null);
  const [filtro, setFiltro] = useState('');

  const { data, isLoading, error } = useQuery<ClienteProcesso[]>({
    queryKey: queryKeys.clientes.processos(clienteId),
    queryFn: () => fetchClienteProcessos(clienteId),
  });
  const search = useSmartSearch(data ?? EMPTY, filtro, CLIENT_PROCESS_SEARCH_FIELDS);
  const visiveis = search.results;

  const atualizarMutation = useMutation({
    mutationFn: () => atualizarProcessosCliente(clienteId),
    onSuccess: (resultado) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.processos(clienteId) });
      if (resultado.novos > 0) {
        queryClient.invalidateQueries({ queryKey: queryKeys.processos.all });
      }
      const avisoDaBusca = AVISOS[resultado.busca as AvisoDaBusca] ? (resultado.busca as AvisoDaBusca) : null;
      setAviso(avisoDaBusca);
      if (resultado.busca === 'indisponivel') {
        showWarning('Data Lake indisponível agora. Tente de novo em instantes.');
      } else if (resultado.busca === 'recusada') {
        showWarning('O Data Lake recusou a busca deste cliente.');
      } else if (resultado.busca === 'ok') {
        showSuccess('Processos atualizados.');
      }
      if (resultado.acima_do_limite > 0) {
        showWarning('Alguns processos ficaram de fora pelo limite do seu plano.');
      }
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Processos do cliente</p>
        <Button
          variant="outline"
          size="sm"
          className={somenteLeitura ? 'hidden' : undefined}
          onClick={() => atualizarMutation.mutate()}
          disabled={atualizarMutation.isPending}
        >
          {atualizarMutation.isPending && !atualizarMutation.isPaused ? (
            <Spinner size={16} className="text-inherit" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {/* Sem rede a mutation fica pausada: dizer "Buscando…" seria falso. */}
          {atualizarMutation.isPaused ? 'Sem conexão' : atualizarMutation.isPending ? 'Buscando…' : 'Atualizar'}
        </Button>
      </div>

      {aviso && (
        <Alert variant="warning">
          <AlertDescription>
            {AVISOS[aviso]}
            {aviso === 'requer_pdpj' && (
              <>
                {' '}
                <Link to="/app/certificados" className="underline">
                  Ir para Certificados
                </Link>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center py-6"><Spinner size={32} label="Carregando processos..." /></div>
      ) : error ? (
        <Alert variant="destructive"><AlertDescription>{classifyError(error).message}</AlertDescription></Alert>
      ) : !data?.length ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Nenhum processo vinculado.</p>
      ) : (
        <div className="space-y-2">
          {data.length > 1 && (
            <BuscaInteligente
              value={filtro}
              onChange={setFiltro}
              strictness={search.strictness}
              onStrictnessChange={search.setStrictness}
              resultCount={visiveis.length}
              totalCount={search.total}
              placeholder="Filtrar por número ou parte"
            />
          )}
          {!visiveis.length && (
            <p className="py-6 text-center text-sm text-muted-foreground">Nenhum processo encontrado.</p>
          )}
          {visiveis.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 rounded-md border border-border/40 p-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={(e) => abrirAutos(p.numero, { evento: e })}
              onMouseDown={(e) => cliqueDoMeioNaLinha(e) && e.preventDefault()}
              onAuxClick={(e) => cliqueDoMeioNaLinha(e) && abrirAutos(p.numero, { evento: e })}
            >
              <Scale className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{p.numero}</p>
                <p className="break-words text-xs text-muted-foreground">
                  {[p.classe, p.tribunal, p.assunto].filter(Boolean).join(' · ')}
                </p>
                {p.parte_adversa && (
                  <p className="break-words text-xs text-muted-foreground">
                    Parte adversa: {p.parte_adversa.nome}
                    {p.parte_adversa.excedente > 0 && ` (+${p.parte_adversa.excedente})`}
                  </p>
                )}
              </div>
              <Badge variant="outline" className="shrink-0 text-xs">{p.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
